/**
 * BelgeTasimaModal.jsx — bir kişi belgesini taşır. Backlog #145 (taşıma) ve #250 (havuz).
 *
 *   mod "tasi"     — başka KATEGORİYE ve/veya aynı firmadaki başka KİŞİYE taşı
 *   mod "havuza"   — "Havuza al": `fileTypeId` 92, yalnızca onay
 *   mod "havuzdan" — havuzdaki dosyayı gerçek bir belge tipine ata (tip ve son geçerlilik tarihi ZORUNLU)
 *
 * Hepsi POST /personnel/{personId}/dosya/{fileId}/tasi (rehber §9, services/personelBelgeService.tasiBelge).
 *
 * ⚠️ BEDELİ ÖNCEDEN SÖYLE: incelenmiş bir belge (kutusunda `reviewStatus` dolu: onay, red ya da bekleme)
 * taşınınca KARARINI KAYBEDER ve Beklemede'ye döner — yoksa personel reddedilmiş bir belgeyi yeni bir
 * kutuya taşıyıp "taze" sanır. İncelenmemiş belge incelenmemiş kalır ve yeni yerinde yeni yüklenmiş gibi
 * sayılır. Cevaptaki `onaySifirlandi` aynı şeyi sonradan söyler; onay penceresi bunu ÖNCE söylüyor.
 *
 * Reddi olduğu gibi göster: 403 (firma yalnızca kendi güncel çalışanları arasında), 400 (KVKK başka
 * kişiye gitmez, ortak firma yok, tarih eksik), 409 (belge birden çok kişide — staff `digerSahipler`
 * adlarını görür, firma yalnızca `sahipSayisi`ni).
 *
 * #297 (backend #295): son geçerlilik tarihini yalnızca PERSONEL (`staff`) girer. Firma girişi tarih HİÇ görmez/göndermez (backend yok
 * sayardı) ve gerçek bir kutuya inen taşıma — incelenmiş olsun olmasın — personel onaylayana kadar Beklemede kalır
 * (`data.incelemeBekliyor`); bu bedel de onaydan ÖNCE söylenir. Havuza almak değişmedi.
 *
 * Kategori listesi `file-types?parentId=1` (kisiAdminService.getKisiFileTypes) eksi 96/98 (paylaşımlı,
 * çalışanları belge üzerinden yönetilir), eski 6/7 (yeni yükleme olarak sunulmuyor) ve kişi kökündeki
 * hatalı yerleşmiş 63/64; havuz (92) ayrı seçenek olarak eklenir — Kişi kökünün çocuğu değil.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import PersonelSecici from "./PersonelSecici";
import TarihGirdisi from "../Common/TarihGirdisi";
import { getKisiFileTypes } from "../../services/kisiAdminService";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import { BELGE_TIP, PAYLASIMLI_TIP_IDS, bugunYmd, tasiBelge } from "../../services/personelBelgeService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const HEDEF_DISI_TIPLER = [...PAYLASIMLI_TIP_IDS, BELGE_TIP.SGK_ESKI, BELGE_TIP.ISG_ESKI, 63, 64];
const KVKK_TIP_ID = 56;
const SEBEP_MAX = 500;
// WAS (until #297): const bugun = () => new Date().toISOString().slice(0, 10); — UTC günü, gece yarısı civarında bir gün geri kalıyordu.
const bugun = bugunYmd;

const BASLIK = { get tasi() { return i18n.t("components:belgeTasimaModal.belgeyiTasi"); }, get havuza() { return i18n.t("components:belgeTasimaModal.havuzaAl"); }, get havuzdan() { return i18n.t("components:belgeTasimaModal.belgeTipineAta"); } };

export default function BelgeTasimaModal({
  mod = "tasi", personId, kisiAd = "", doc, belgeAdi, firmaId, staff = false, onClose, onDone,
}) {
  const { t: tx } = useTranslation("components");
  const [tipler, setTipler] = useState(null);
  const [hedefTip, setHedefTip] = useState(mod === "havuza" ? String(BELGE_TIP.HAVUZ) : "");
  const [baskaKisi, setBaskaKisi] = useState(false);
  const [hedefKisi, setHedefKisi] = useState([]);
  const [tarih, setTarih] = useState("");
  const [sebep, setSebep] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sahipler, setSahipler] = useState(null);

  useEffect(() => {
    if (mod === "havuza") return undefined;
    let ignore = false;
    async function run() {
      try {
        const rows = await getKisiFileTypes();
        if (!ignore) setTipler(rows);
      } catch (e) {
        if (!ignore) { setError(e.message); setTipler([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [mod]);

  const incelenmis = doc?.reviewStatus != null;
  const incelemeAdi = incelenmis ? FILE_APPROVAL_STATUS[doc.reviewStatus]?.label ?? tx("belgeTasimaModal.incelenmis") : null;
  const kvkk = Number(doc?.fileTypeId) === KVKK_TIP_ID;
  const havuzaGidiyor = Number(hedefTip) === BELGE_TIP.HAVUZ;
  // Gerçek bir kutuya inen belgenin son kullanma tarihi olmalı: tarihsiz belge sonsuza dek sayılır. Tarihi PERSONEL girer (#297);
  // firma girişinde alan yok — belge gerçek bir kutuya inerken Beklemede kalır ve tarihi onaylayan personel yazar.
  // WAS (until #297): const tarihGerekli = !havuzaGidiyor && (mod === "havuzdan" || !doc?.expiryDate);
  const tarihGerekli = staff && !havuzaGidiyor && (mod === "havuzdan" || !doc?.expiryDate);
  const firmaBeklemeyeDuser = !staff && !havuzaGidiyor;
  const tarihGecmis = tarih && tarih < bugun();

  const secenekler = (tipler ?? []).filter(
    (t) => !HEDEF_DISI_TIPLER.includes(t.fileTypeId) && t.fileTypeId !== Number(doc?.fileTypeId),
  );

  const tipSecildi = hedefTip !== "";
  const kisiSecildi = baskaKisi && hedefKisi.length === 1;
  const degisiklikVar = mod === "havuza" || tipSecildi || kisiSecildi;
  const gecerli = degisiklikVar
    && (!baskaKisi || kisiSecildi)
    && (!tarihGerekli || (tarih && !tarihGecmis))
    && sebep.length <= SEBEP_MAX
    && !(mod === "havuzdan" && !tipSecildi);

  async function submit() {
    setBusy(true);
    setError(null);
    setSahipler(null);
    try {
      const sonuc = await tasiBelge(personId, doc.fileId, {
        hedefPersonId: kisiSecildi ? hedefKisi[0] : undefined,
        fileTypeId: tipSecildi ? hedefTip : undefined,
        gecerlilikTarihi: staff && !havuzaGidiyor && tarih ? tarih : undefined,
        sebep,
      });
      onDone(sonuc);
    } catch (e) {
      setError(e.message);
      if (e.status === 409 && e.data) setSahipler(e.data);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={busy ? undefined : onClose} size={baskaKisi ? "lg" : undefined} scrollable>
      <ModalHeader toggle={busy ? undefined : onClose}>{BASLIK[mod]}</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <div className="fw-semibold">{belgeAdi || doc?.fileName || tx("belgeTasimaModal.dosya", { fileId: doc?.fileId })}</div>
          <div className="small text-muted">{kisiAd}{doc?.expiryDate ? tx("belgeTasimaModal.sonGecerlilik", { v: doc.expiryDate.split("-").reverse().join(".") }) : ""}</div>
        </div>

        {/* Bedel — onaydan ÖNCE. */}
        {mod !== "havuzdan" && (
          incelenmis ? (
            <Alert fade={false} color="warning" className="small py-2">
              {tx("belgeTasimaModal.buBelgeDahaOnceIncelenmis")}<strong>{incelemeAdi}</strong>{tx("belgeTasimaModal.tasinincaBuKarar")}{" "}<strong>{tx("belgeTasimaModal.silinir")}</strong>{tx("belgeTasimaModal.belgeYeniden")}{" "}<strong>{tx("belgeTasimaModal.beklemede")}</strong>{" "}{tx("belgeTasimaModal.olurVeYenidenInceleneneKadar")}
            </Alert>
          ) : firmaBeklemeyeDuser ? (
            <Alert fade={false} color="info" className="small py-2">
              {tx("belgeTasimaModal.firmaBeklemedeKalir")}
            </Alert>
          ) : (
            <Alert fade={false} color="info" className="small py-2">
              {tx("belgeTasimaModal.belgeHenuzIncelenmemisYeniYerinde")}
            </Alert>
          )
        )}
        {mod === "havuzdan" && firmaBeklemeyeDuser && (
          <Alert fade={false} color="info" className="small py-2">
            {tx("belgeTasimaModal.firmaBeklemedeKalir")}
          </Alert>
        )}

        {error && (
          <Alert fade={false} color="danger" className="small py-2">
            {error}
            {sahipler?.digerSahipler?.length > 0 && (
              <ul className="mb-0 mt-1">
                {sahipler.digerSahipler.map((s) => <li key={s.personId}>{s.ad} {s.soyad} (#{s.personId})</li>)}
              </ul>
            )}
            {sahipler?.sahipSayisi != null && <div className="mt-1">{tx("belgeTasimaModal.belgeKisideKayitli", { sahipSayisi: sahipler.sahipSayisi })}</div>}
          </Alert>
        )}

        {mod !== "havuza" && (
          <FormGroup>
            <Label className="small mb-1">{mod === "havuzdan" ? tx("belgeTasimaModal.belgeTipi") : tx("belgeTasimaModal.hedefKategori")}</Label>
            {tipler === null ? (
              <div><Spinner size="sm" /></div>
            ) : (
              <Input type="select" bsSize="sm" value={hedefTip} onChange={(e) => setHedefTip(e.target.value)}>
                <option value="">{mod === "havuzdan" ? tx("belgeTasimaModal.secin") : tx("belgeTasimaModal.kategoriDegismesin")}</option>
                {secenekler.map((t) => <option key={t.fileTypeId} value={t.fileTypeId}>{t.fileTypeName}</option>)}
                {mod === "tasi" && Number(doc?.fileTypeId) !== BELGE_TIP.HAVUZ && (
                  <option value={BELGE_TIP.HAVUZ}>{tx("belgeTasimaModal.havuzaAlBelgeTipiSonra")}</option>
                )}
              </Input>
            )}
          </FormGroup>
        )}

        {/* Başka kişiye taşıma "aynı firmanın güncel personeli" arasından seçer: firma bağlamı yoksa (firmasız kişi
            sayfası, #292) seçilecek liste de yok, onay kutusu hiç sunulmaz. */}
        {mod === "tasi" && firmaId && (
          <FormGroup check className="mb-3">
            <Input
              id="tasi-baska-kisi" type="checkbox" checked={baskaKisi} disabled={kvkk}
              onChange={(e) => { setBaskaKisi(e.target.checked); setHedefKisi([]); }}
            />
            <Label check for="tasi-baska-kisi" className="small">
              {tx("belgeTasimaModal.baskaKisiyeTasi")}{" "}<span className="text-muted">{tx("belgeTasimaModal.ayniFirmaninGuncelPersoneli")}{kvkk && tx("belgeTasimaModal.kvkkBaskaKisiyeTasinamaz")}</span>
            </Label>
          </FormGroup>
        )}
        {mod === "tasi" && baskaKisi && (
          <div className="mb-3">
            <PersonelSecici firmaId={firmaId} secili={hedefKisi} onChange={setHedefKisi} tekli haricKisiId={personId} />
            {kisiSecildi && (
              <div className="small text-muted mt-2">
                {tx("belgeTasimaModal.belgeBuKisiyeGecerVe")}{" "}{kisiAd || tx("belgeTasimaModal.buKiside")}{" "}{tx("belgeTasimaModal.artikGorunmez")}
              </div>
            )}
          </div>
        )}

        {tarihGerekli && (
          <FormGroup>
            <Label className="small mb-1">{tx("belgeTasimaModal.sonGecerlilikTarihi")}</Label>
            <TarihGirdisi min={bugun()} value={tarih} invalid={!!tarihGecmis} onChange={setTarih} />
            <div className="form-text small">
              {mod === "havuzdan"
                ? tx("belgeTasimaModal.havuzdakiDosyaninTarihiYokGercek")
                : tx("belgeTasimaModal.belgeninSonGecerlilikTarihiKayitli")}
            </div>
          </FormGroup>
        )}

        <FormGroup className="mb-0">
          <Label className="small mb-1">{tx("belgeTasimaModal.sebep")}{" "}<span className="text-muted">{tx("belgeTasimaModal.istegeBagli")}</span></Label>
          <Input
            type="textarea" rows={2} value={sebep} invalid={sebep.length > SEBEP_MAX}
            placeholder={tx("belgeTasimaModal.ornYanlisKisiyeYuklenmis")} onChange={(e) => setSebep(e.target.value)}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{tx("belgeTasimaModal.vazgec")}</Button>
        <Button color={incelenmis && mod !== "havuzdan" ? "warning" : "primary"} onClick={submit} disabled={busy || !gecerli}>
          {busy ? <Spinner size="sm" /> : mod === "havuza" ? tx("belgeTasimaModal.havuzaAl") : mod === "havuzdan" ? tx("belgeTasimaModal.ata") : tx("belgeTasimaModal.tasi")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Taşıma cevabından kullanıcıya gösterilecek tek satır. `incelemeBekliyor` (#297, backend #295): firma girişinin gerçek bir kutuya
 * indirdiği belge — incelenmiş olsun olmasın — personel onaylayana kadar Beklemede kalır ve hiçbir şeye sayılmaz.
 */
export function tasimaMesaji(sonuc, { havuza = false } = {}) {
  const taban = havuza || sonuc?.havuzda ? i18n.t("components:belgeTasimaModal.belgeHavuzaAlindi") : i18n.t("components:belgeTasimaModal.belgeTasindi");
  if (sonuc?.incelemeBekliyor) {
    // Önceki kararın silindiği ayrıca söylenir; ikisi de "Beklemede" dediği için tek cümleye birleşir.
    return i18n.t(sonuc.onaySifirlandi ? "components:belgeTasimaModal.firmaKararSilindiFdsBekliyor" : "components:belgeTasimaModal.firmaFdsBekliyor", { taban });
  }
  return sonuc?.onaySifirlandi ? i18n.t("components:belgeTasimaModal.oncekiKararSilindiBelgeBeklemede", { taban }) : taban;
}
